import { useEffect, useState } from "react";

import { useDataProvider } from "@refinedev/core";

export interface UseSiteFacetsArgs {
  contentType?: string;
  /** Source group ("web"/"instagram"). Mutually exclusive with `xStrategy`. */
  sourceKind?: string;
  /** Specific web strategy ("api"/"simple"/"sitemap"). */
  xStrategy?: string;
  enabled?: boolean;
  countryCode?: string;
}

/** A city and the country it is in (the same city name can sit under two countries). */
export interface CityFacet {
  city: string;
  country_code: string;
}

export interface UseSiteFacetsResult {
  countries: string[];
  cities: string[];
  /** Every city with its country. Like `countries`, NOT narrowed by `countryCode`. */
  cityCountries: CityFacet[];
  isLoading: boolean;
}

interface SiteFacetsResponse {
  countries: string[];
  cities: string[];
  city_countries?: CityFacet[];
}

/**
 * Fetches the bounded `/sites/facets` lists (distinct countries / cities) used
 * to populate the sites list toolbar selects. `countries` is scoped by
 * content_type / x_strategy / enabled; `cities` is additionally scoped by
 * country_code. `cityCountries` has the scope of `countries`: every city, each
 * with its country, so a picker can search a city without choosing a country. Re-fetches whenever the scoping args change. Errors degrade to
 * empty lists so the toolbar stays usable.
 */
export function useSiteFacets(args: UseSiteFacetsArgs): UseSiteFacetsResult {
  const { custom } = useDataProvider()("tina");
  const { contentType, sourceKind, xStrategy, enabled, countryCode } = args;

  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [cityCountries, setCityCountries] = useState<CityFacet[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadFacets() {
      setIsLoading(true);

      const query: Record<string, string | boolean> = {};
      if (contentType) query.content_type = contentType;
      if (sourceKind) query.source_kind = sourceKind;
      if (xStrategy) query.x_strategy = xStrategy;
      if (enabled !== undefined) query.enabled = enabled;
      if (countryCode) query.country_code = countryCode;

      try {
        const response = await custom!({
          url: "",
          method: "get",
          query,
          meta: { urlPath: "sites/facets" },
        });
        const body = response.data as SiteFacetsResponse;
        if (isMounted) {
          setCountries(body.countries ?? []);
          setCities(body.cities ?? []);
          setCityCountries(body.city_countries ?? []);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[use-site-facets] failed to load facets", error);
        if (isMounted) {
          setCountries([]);
          setCities([]);
          setCityCountries([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadFacets();

    return () => {
      isMounted = false;
    };
  }, [custom, contentType, sourceKind, xStrategy, enabled, countryCode]);

  return { countries, cities, cityCountries, isLoading };
}
